import React, { useState, useCallback } from 'react'
import styled from 'styled-components'
import { up } from 'styled-breakpoints'
import { useTranslation } from 'react-i18next'

import { useBookSearch } from '../../../../data/hooks/book'
import { useScrollTrigger } from '../../../../data/hooks/interface/scroll-trigger'
import { DialogLevel, useDialog } from '../../dialog/hook'

import Loader from '../../loader'
import BookDialog from '../book'
import ColoredDialogBackdrop from '../colored-dialog-backdrop'
import SearchInput from '../../search-input'

// TODO: move to common?
import BookRow from '../../../features/search/pages/search/book'

type FavoriteBookProps = {
  onOnboardingFinished: () => void
}

const FavoriteBook: React.FunctionComponent<FavoriteBookProps> = ({ onOnboardingFinished }) => {
    const { t } = useTranslation("search")
    const { t: onboardingT } = useTranslation("onboarding")
    const { setComponent } = useDialog()

    const [q, setQ] = useState("")

    const [state, update] = useBookSearch()
    const { books, query, pending, final, error } = state

    const onSearch = useCallback((q: string) => {
        if (final && q === query) {
            return
        }

        update(q, 0, 20, true)
    }, [query])

    const [scrollParent, setScrollParent] = useState<HTMLDivElement | null>(null)
    const [_, setScrollable] = useScrollTrigger({
        onBottomReached: () => update(q, books?.length || 0, 20),
        bottomStopCondition: () => pending || final,
        dependencies: [q, books],
        scrollParent,
    })


    return (
        <Container>
            <Title>{onboardingT("favorite_book.title")}</Title>
            <InputLine>
                <SearchInput 
                    value={q} 
                    placeholder={t("placeholder")} 
                    onChange={q => setQ(q)}
                    onSearch={onSearch}
                />
            </InputLine>
            <Results ref={r => setScrollParent(r)}>
                { books &&
                    <BookResults ref={r => setScrollable(r)}>
                        {
                            books?.map((b, i) => <BookRow 
                                key={i} 
                                book={b} 
                                onClick={() => setComponent(
                                    <BookDialog book={b} needsUpdate={true} onStatusUpdated={onOnboardingFinished}/>, 
                                    <ColoredDialogBackdrop bg={b.bg_color}/>, 
                                    DialogLevel.Top
                                )} 
                            />)
                        }
                        {
                            pending && <Loader />
                        }
                    </BookResults>
                }
            </Results>
        </Container>
    )
}

const Container = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    grid-row-gap: 32px;

    justify-items: center;

    height: 100%;
    width: 100%;
`

const Title = styled.div`
    text-align: center;
    width: 100%;

    font-size: 16px;
    font-weight: bold;
`

const InputLine = styled.div`
    width: 100%;
    height: 48px;
`

const Results = styled.div`
    display: grid;
    align-items: center;
    justify-items: center;

    width: 100%;

    overflow-y: auto;

    ${up('tablet')} {
        max-height: 70vh;
    }
`

const BookResults = styled.div`
    display: grid;
    justify-items: center;
`


export default FavoriteBook